import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_ITEM_COMPONENTS_LOAD,
    ACTION_ITEM_COMPONENTS_LOAD_BY_ID,
    ACTION_ITEM_COMPONENTS_LOAD_FAILURE,
    ACTION_ITEM_COMPONENTS_LOAD_SUCCESS,

    ACTION_ITEM_COMPONENTS_SAVE,
    ACTION_ITEM_COMPONENTS_SAVE_SUCCESS,
    ACTION_ITEM_COMPONENTS_SAVE_FAILURE,

    ACTION_ITEM_COMPONENTS_CREATE,
    ACTION_ITEM_COMPONENTS_CREATE_SUCCESS,
    ACTION_ITEM_COMPONENTS_CREATE_FAILURE,

    ACTION_ITEM_COMPONENTS_CLEAR,
    ACTION_ITEM_COMPONENTS_FILTER,

    ItemComponentAction
} from '../actions/itemComponents';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IItemComponentState {
    formData: Dtos.ItemComponent | Dtos.ItemComponent[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.ItemComponent[];
    formLength: number;
}

const initialState: IItemComponentState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: IItemComponentState, isLoading: boolean): IItemComponentState {
    const newState: IItemComponentState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: IItemComponentState,
    formData: Dtos.ItemComponent | Dtos.ItemComponent[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IItemComponentState {
    const newState: IItemComponentState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: IItemComponentState, responseStatus: Dtos.ResponseStatus): IItemComponentState {
    const newState: IItemComponentState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: IItemComponentState, formFilter: IFormFilter): IItemComponentState {

    const items: Dtos.ItemComponent[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p.name.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: IItemComponentState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function saving(state: IItemComponentState, isSaving: boolean): IItemComponentState {
    const newState: IItemComponentState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IItemComponentState,
    formData: Dtos.ItemComponent | Dtos.ItemComponent[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IItemComponentState {
    const newState: IItemComponentState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IItemComponentState, responseStatus: Dtos.ResponseStatus): IItemComponentState {
    const newState: IItemComponentState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IItemComponentState, isCreating: boolean): IItemComponentState {
    const newState: IItemComponentState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IItemComponentState,
    formData: Dtos.ItemComponent,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IItemComponentState {
    const newState: IItemComponentState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IItemComponentState, responseStatus: Dtos.ResponseStatus): IItemComponentState {
    const newState: IItemComponentState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function clear(state: IItemComponentState, propertiesToClear?: string[]): IItemComponentState {

    if (!propertiesToClear) {
        let formFilter = state.formFilter;
        const newState: IItemComponentState = update(initialState, {
            formFilter: {
                $set: formFilter
            }
        });
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

const ItemComponentReducer: Reducer<IItemComponentState> = (state: IItemComponentState = initialState, action: ItemComponentAction) => {
    switch (action.type) {
        case ACTION_ITEM_COMPONENTS_LOAD:
        case ACTION_ITEM_COMPONENTS_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_ITEM_COMPONENTS_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_ITEM_COMPONENTS_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_ITEM_COMPONENTS_SAVE:
            return saving(state, true);
        case ACTION_ITEM_COMPONENTS_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_ITEM_COMPONENTS_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_ITEM_COMPONENTS_CREATE:
            return creating(state, true);
        case ACTION_ITEM_COMPONENTS_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_ITEM_COMPONENTS_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_ITEM_COMPONENTS_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_ITEM_COMPONENTS_FILTER:
            return filter(state, action.formFilter);
    }

    return state;
}

export default ItemComponentReducer;